<template>
  <div class="customers-page py-3">
    <header class="customers-toolbar">
      <div class="customers-toolbar__title">
        <span class="customers-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Customers</h1>
          <p>Browse, search, and manage CRM contacts across orders and bottles</p>
        </div>
      </div>
      <div class="customers-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="customers-updated">Updated {{ lastUpdatedLabel }}</span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="reload"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          size="sm"
          class="export-btn"
          :class="{ 'is-loading': isExporting }"
          :disabled="isExporting || isLoading || !totalRecords"
          @click="exportCustomers"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="export-btn__icon" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>{{ isExporting ? 'Exporting…' : 'Export' }}</span>
        </CButton>
        <CButton
          v-if="!isModerator"
          color="primary"
          size="sm"
          class="create-btn"
          @click="openAddCustomerModal"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Add Customer</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="customers-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        :class="['summary-tile', `summary-tile--${card.key}`]"
      >
        <span class="summary-tile__label">{{ card.label }}</span>
        <span class="summary-tile__value">{{ card.value }}</span>
      </div>
    </div>

    <section class="customers-section">
      <header class="customers-section__head">
        <h2>Filters</h2>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="customers-filter-clear"
          @click="resetFilters"
        >Clear filters</button>
      </header>
      <div class="customers-section__body">
        <div class="customers-filters">
          <div class="customers-filter">
            <label class="customers-filter__label">State</label>
            <AppDropdown
              v-model="filters.state"
              :options="stateOptions"
              searchable
              search-placeholder="Search state…"
              placeholder="All states"
            />
          </div>
          <div class="customers-filter">
            <label class="customers-filter__label">District</label>
            <AppDropdown
              v-model="filters.district"
              :options="districtOptions"
              searchable
              search-placeholder="Search district…"
              placeholder="All districts"
            />
          </div>
          <div class="customers-filter">
            <label class="customers-filter__label">Country</label>
            <AppDropdown
              v-model="filters.country"
              :options="countryOptions"
              searchable
              search-placeholder="Search country…"
              placeholder="All countries"
            />
          </div>
          <div class="customers-filter customers-filter--action">
            <label class="customers-filter__label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="customers-apply-btn" :disabled="isLoading" @click="applyFilters">Apply</button>
          </div>
          <div class="customers-filter customers-filter--action">
            <label class="customers-filter__label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="customers-reset-btn" :disabled="isLoading || (!hasActiveFilters && !filters.state && !filters.district && !filters.country)" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <section class="customers-section">
      <header class="customers-section__head">
        <h2>{{ isFilterOrSearchActive ? 'Search Results' : 'All Customers' }}</h2>
        <span v-if="!isLoading && rows.length" class="customers-count">{{ paginationLabel }}</span>
      </header>

      <div class="customers-section__body customers-search-row">
        <form class="customers-search-wrap" @submit.prevent="onSearchSubmit">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="customers-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="customers-input"
            placeholder="Search by name or phone…"
          />
          <button
            v-if="searchTerm || isSearchActive"
            type="button"
            class="customers-search-clear"
            aria-label="Clear search"
            @click="clearSearch"
          >×</button>
        </form>
      </div>

      <div v-if="isLoading" class="customers-section__body customers-loading">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading customers…</span>
      </div>

      <div v-else-if="!rows.length" class="customers-section__body customers-empty">
        <div class="customers-empty__title">
          {{ isFilterOrSearchActive ? 'No matching customers' : 'No customers yet' }}
        </div>
        <div class="customers-empty__hint">
          <template v-if="isFilterOrSearchActive">
            Try adjusting your search or filters —
            <button type="button" class="customers-empty__link" @click="clearAll">clear all</button>.
          </template>
          <template v-else>
            Customers you create will appear here.
          </template>
        </div>
      </div>

      <div v-else>
        <div class="customers-table-wrap">
          <table class="customers-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>District</th>
                <th class="col-num">Orders</th>
                <th class="col-num">Bottles</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(customer, index) in rows" :key="customer.id || `row-${index}`" class="customer-row">
                <td class="col-customer">
                  <div class="customer-cell">
                    <div class="customer-avatar" :style="{ background: avatarColor(customer) }">
                      {{ initials(customer.name) }}
                    </div>
                    <div>
                      <div class="customer-name">{{ customer.name || 'Unnamed' }}</div>
                      <div v-if="customer.id" class="customer-sub">#{{ customer.id }}</div>
                    </div>
                  </div>
                </td>
                <td class="col-phone">
                  <span v-if="customer.phone" class="customer-phone">{{ customer.phone }}</span>
                  <span v-else class="customer-dim">—</span>
                </td>
                <td class="col-district">
                  <span v-if="districtLabel(customer)" class="district-pill">{{ districtLabel(customer) }}</span>
                  <span v-else class="customer-dim">—</span>
                </td>
                <td class="col-num">{{ ordersCount(customer) }}</td>
                <td class="col-num">{{ Number(customer.bottles || 0) }}</td>
                <td class="col-actions">
                  <CButton color="primary" size="sm" class="tbl-btn" @click="viewCustomer(customer)">View</CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="customers-pagination">
          <AppPagination
            :value="currentPage"
            :pages="totalPages"
            :disabled="isLoading"
            @change="onPageChange"
          />
        </div>
      </div>
    </section>

    <AppConfirmDialog
      :visible="addCustomerModal"
      title="Add new customer"
      message="Capture the contact's basic information to add them to your CRM."
      confirm-label="Save Contact"
      busy-label="Saving…"
      cancel-label="Cancel"
      variant="info"
      :busy="isSavingContact"
      @confirm="saveContact"
      @cancel="cancelAddCustomer"
    >
      <div class="form-grid">
        <div class="form-field">
          <label class="form-label" for="cust-name">Customer name</label>
          <input
            id="cust-name"
            v-model.trim="newCustomer.name"
            type="text"
            class="form-input"
            autocomplete="name"
            placeholder="e.g. Anitha Raj"
          />
        </div>
        <div class="form-field">
          <label class="form-label" for="cust-phone">Phone number</label>
          <input
            id="cust-phone"
            v-model.trim="newCustomer.phone"
            type="tel"
            inputmode="numeric"
            maxlength="15"
            class="form-input"
            autocomplete="tel"
            placeholder="10-digit phone"
          />
        </div>
      </div>
    </AppConfirmDialog>

    <AppConfirmDialog
      :visible="resultModal.show"
      :title="resultModal.title || 'Notice'"
      :message="resultModal.message"
      :variant="resultModalVariant"
      confirm-label="OK"
      cancel-label=""
      @confirm="onResultModalOk"
      @cancel="onResultModalOk"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import AppPagination from '@/components/AppPagination.vue'
import AppDropdown from '@/components/AppDropdown.vue'

export default {
  name: 'Customers',
  components: { AppConfirmDialog, AppPagination, AppDropdown },
  data () {
    return {
      rows: [],
      searchTerm: '',
      appliedSearchTerm: '',
      isSearchActive: false,
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      isLoading: false,
      isExporting: false,
      isSavingContact: false,
      addCustomerModal: false,
      errorMessage: '',
      lastUpdatedAt: null,
      filters: {
        state: '',
        district: '',
        country: ''
      },
      appliedFilters: {
        state: '',
        district: '',
        country: ''
      },
      filterOptions: {
        states: [],
        districts: [],
        countries: []
      },
      newCustomer: {
        name: '',
        phone: ''
      },
      resultModal: {
        show: false,
        title: '',
        message: '',
        color: 'primary'
      }
    }
  },
  computed: {
    isModerator () {
      const roles = String(localStorage.getItem('roles') || '')
      return roles.toLowerCase().split(',').map((v) => v.trim()).includes('moderator')
    },
    hasActiveFilters () {
      return !!(this.appliedFilters.state || this.appliedFilters.district || this.appliedFilters.country)
    },
    isFilterOrSearchActive () {
      return this.isSearchActive || this.hasActiveFilters
    },
    stateOptions () {
      return [{ value: '', label: 'All states' }].concat(
        (this.filterOptions.states || []).map((v) => ({ value: v, label: v }))
      )
    },
    districtOptions () {
      return [{ value: '', label: 'All districts' }].concat(
        (this.filterOptions.districts || []).map((v) => ({ value: v, label: v }))
      )
    },
    countryOptions () {
      return [{ value: '', label: 'All countries' }].concat(
        (this.filterOptions.countries || []).map((v) => ({ value: v, label: v }))
      )
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    summaryCards () {
      const districts = new Set(
        this.rows.map((row) => this.districtLabel(row)).filter(Boolean)
      )
      const pageOrders = this.rows.reduce((sum, row) => sum + this.ordersCount(row), 0)
      const pageBottles = this.rows.reduce((sum, row) => sum + Number(row.bottles || 0), 0)
      const total = this.totalRecords || this.rows.length

      return [
        { key: 'total', label: this.isFilterOrSearchActive ? 'Matches' : 'Total Customers', value: total },
        { key: 'orders', label: 'Orders (Page)', value: pageOrders },
        { key: 'bottles', label: 'Bottles (Page)', value: pageBottles },
        { key: 'districts', label: 'Districts (Page)', value: districts.size }
      ]
    },
    paginationLabel () {
      const total = this.totalRecords
      const noun = total === 1 ? 'customer' : 'customers'
      if (this.isFilterOrSearchActive && !total) {
        return `${this.rows.length} on page`
      }
      if (!total || this.totalPages <= 1) {
        return `${this.rows.length} ${this.rows.length === 1 ? 'customer' : 'customers'}`
      }
      const perPage = this.rows.length
      const start = (this.currentPage - 1) * perPage + 1
      const end = Math.min(start + perPage - 1, total)
      return `${start}–${end} of ${total} ${noun}`
    },
    resultModalVariant () {
      const color = String(this.resultModal.color || '').toLowerCase()
      if (color === 'success') return 'success'
      if (color === 'danger' || color === 'warning') return color
      return 'info'
    }
  },
  created () {
    this.fetchFilterOptions()
    this.fetchContacts(1)
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    initials (name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    avatarColor (row) {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(row?.name || row?.id || row?.phone || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      }
      return palette[hash % palette.length]
    },
    districtLabel (row) {
      const address = Array.isArray(row?.address) ? row.address[0] : null
      const district = address?.district || row?.district || ''
      return district ? String(district).trim() : ''
    },
    ordersCount (row) {
      if (Array.isArray(row?.order)) return row.order.length
      if (typeof row?.orders_count === 'number') return row.orders_count
      return 0
    },
    viewCustomer (row) {
      if (!row || !row.id) return
      this.$router.push({ path: `/customer/${row.id}/orders` })
    },
    openAddCustomerModal () {
      this.errorMessage = ''
      this.addCustomerModal = true
    },
    cancelAddCustomer () {
      if (this.isSavingContact) return
      this.addCustomerModal = false
    },
    reload () {
      this.fetchContacts(this.currentPage || 1)
    },
    queryParams () {
      const params = { token: this.token() }
      if (this.appliedSearchTerm) params.search = this.appliedSearchTerm
      if (this.appliedFilters.state) params.state = this.appliedFilters.state
      if (this.appliedFilters.district) params.district = this.appliedFilters.district
      if (this.appliedFilters.country) params.country = this.appliedFilters.country
      return params
    },
    fetchFilterOptions () {
      axios.get(`${this.$apiAdress}/api/contacts/filters/options`, { params: { token: this.token() } })
        .then((response) => {
          const payload = response.data || {}
          this.filterOptions = {
            states: Array.isArray(payload.states) ? payload.states : [],
            districts: Array.isArray(payload.districts) ? payload.districts : [],
            countries: Array.isArray(payload.countries) ? payload.countries : []
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    fetchContacts (page) {
      this.errorMessage = ''
      this.isLoading = true

      const params = { ...this.queryParams(), page: page || 1 }

      axios.get(`${this.$apiAdress}/api/contacts`, { params })
        .then((response) => {
          const payload = response.data || {}
          const data = Array.isArray(payload.data) ? payload.data : []
          this.rows = data
          this.currentPage = Number(payload.current_page || page || 1)
          this.totalPages = Number(payload.last_page || 1)
          this.totalRecords = Number(payload.total || data.length)
          this.isSearchActive = !!this.appliedSearchTerm
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load contacts.'
          this.rows = []
          this.totalRecords = 0
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onSearchSubmit () {
      this.appliedSearchTerm = this.searchTerm
      this.fetchContacts(1)
    },
    clearSearch () {
      this.searchTerm = ''
      this.appliedSearchTerm = ''
      this.isSearchActive = false
      this.fetchContacts(1)
    },
    applyFilters () {
      this.appliedFilters = {
        state: this.filters.state,
        district: this.filters.district,
        country: this.filters.country
      }
      this.fetchContacts(1)
    },
    resetFilters () {
      this.filters = { state: '', district: '', country: '' }
      this.appliedFilters = { state: '', district: '', country: '' }
      this.fetchContacts(1)
    },
    clearAll () {
      this.searchTerm = ''
      this.appliedSearchTerm = ''
      this.isSearchActive = false
      this.filters = { state: '', district: '', country: '' }
      this.appliedFilters = { state: '', district: '', country: '' }
      this.fetchContacts(1)
    },
    exportCustomers () {
      if (this.isExporting) return
      this.isExporting = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/contacts/export/excel`, {
        params: this.queryParams(),
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          const stamp = new Date().toISOString().slice(0, 10)
          link.href = url
          link.download = `customers_${stamp}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to export customers.'
        })
        .finally(() => {
          this.isExporting = false
        })
    },
    onPageChange (page) {
      const target = Number(page || 1)
      if (target === this.currentPage) return
      this.currentPage = target
      this.fetchContacts(target)
    },
    saveContact () {
      this.isSavingContact = true

      axios.post(`${this.$apiAdress}/api/contact/save?token=${this.token()}`, {
        name: this.newCustomer.name,
        phone: this.newCustomer.phone
      })
        .then((response) => {
          const data = response.data || {}
          this.resultModal = {
            show: true,
            title: data.title || 'Success',
            message: data.message || 'Customer created successfully.',
            color: data.color || 'success'
          }
        })
        .catch((error) => {
          const data = error?.response?.data || {}
          const validation = data?.error && typeof data.error === 'object'
            ? Object.values(data.error).flat().join(', ')
            : ''

          this.resultModal = {
            show: true,
            title: data.title || 'Error',
            message: data.message || validation || 'Unable to save contact.',
            color: data.color || 'warning'
          }
        })
        .finally(() => {
          this.isSavingContact = false
        })
    },
    onResultModalOk () {
      const isSuccess = String(this.resultModal.title || '').toLowerCase() === 'success'
      this.resultModal.show = false

      if (!isSuccess) return

      this.addCustomerModal = false
      this.newCustomer = { name: '', phone: '' }
      this.searchTerm = ''
      this.fetchContacts(1)
    }
  }
}
</script>

<style scoped>
.customers-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.customers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.customers-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.customers-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.customers-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.customers-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.customers-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.customers-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.customers-toolbar__actions >>> .btn.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.customers-toolbar__actions >>> .btn.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.customers-toolbar__actions >>> .btn.create-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-btn__icon {
  flex-shrink: 0;
  display: block;
}

.customers-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.customers-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.customers-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.customers-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.customers-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Export button ── */
.customers-toolbar__actions >>> .btn.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.customers-toolbar__actions >>> .btn.export-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.customers-toolbar__actions >>> .btn.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.customers-toolbar__actions >>> .btn.export-btn.is-loading {
  cursor: progress;
}

.export-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
}

/* ── Filters row ── */
.customers-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.customers-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 220px;
  min-width: 0;
}

.customers-filter--action {
  flex: 0 0 auto;
  min-width: 110px;
}

.customers-filter__label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.customers-apply-btn,
.customers-reset-btn {
  width: 100%;
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.customers-apply-btn {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.customers-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.customers-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.customers-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.customers-apply-btn:disabled,
.customers-reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.customers-apply-btn:focus,
.customers-reset-btn:focus {
  outline: none;
}

.customers-apply-btn:focus-visible,
.customers-reset-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.customers-filter-clear {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.customers-filter-clear:hover {
  text-decoration: underline;
}

.customers-filter-clear:focus {
  outline: none;
}

.customers-filter-clear:focus-visible {
  outline: none;
  text-decoration: underline;
}

/* ── Alert ── */
.customers-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total     { border-left-color: #1f2a44; }
.summary-tile--orders    { border-left-color: #16a07a; }
.summary-tile--bottles   { border-left-color: #6d28d9; }
.summary-tile--districts { border-left-color: #0ea5e9; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Section card ── */
.customers-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.customers-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.customers-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.customers-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.customers-section__body {
  padding: 1rem;
}

/* ── Search ── */
.customers-search-row {
  border-bottom: 1px solid #eef1f7;
}

.customers-search-wrap {
  position: relative;
  max-width: 520px;
}

.customers-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.customers-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.customers-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.customers-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.customers-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

/* ── Loading / empty ── */
.customers-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.customers-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.customers-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.customers-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.customers-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.customers-empty__link:hover {
  text-decoration: underline;
}

/* ── Table ── */
.customers-table-wrap {
  overflow-x: auto;
}

.customers-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.customers-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-top: 0;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.65rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #374151;
  white-space: nowrap;
  text-align: left;
}

.customers-table tbody tr {
  transition: background-color 0.12s ease;
}

.customers-table tbody tr:hover {
  background: #f8fafc;
}

.customers-table td {
  padding: 0.85rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  vertical-align: middle;
  color: #374151;
}

.col-customer { min-width: 220px; }
.col-phone    { min-width: 140px; }
.col-district { min-width: 140px; }
.col-num      { width: 92px; text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; color: #1f2a44; }
.col-actions  { width: 90px; text-align: right; white-space: nowrap; }

/* ── Customer cell ── */
.customer-cell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.customer-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.customer-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.customer-sub {
  font-size: 0.73rem;
  color: #6b7280;
  line-height: 1.4;
}

.customer-phone {
  font-size: 0.85rem;
  color: #1f2a44;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.customer-dim {
  color: #9ca3af;
  font-size: 0.82rem;
}

/* ── District pill ── */
.district-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
  background: #e0edff;
  color: #1e40af;
}

/* ── Action button ── */
.tbl-btn {
  border-radius: 7px !important;
  font-weight: 600;
  font-size: 0.78rem;
}

/* ── Pagination ── */
.customers-pagination {
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
}

/* ── Form fields (Add Customer dialog) ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .customers-toolbar {
    align-items: flex-start;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }

  .customers-search-wrap {
    max-width: 100%;
  }
}
</style>
